/**
 * El seguimiento de los documentos, como lo describe el contrato con el API.
 *
 * A mano hasta que `pnpm api:types` traiga `list_document_reviews` y el
 * `get_document_review` ampliado del API de Socio Amigo (los nombres de las
 * herramientas son los del núcleo y se ajustan cuando salga su OpenAPI). Los
 * campos que el contrato AGREGA al detalle van opcionales: la pantalla los
 * saca de lo que ya llega (ver `detail.ts`).
 */
import type { operations } from "../../lib/api-types.ts";

import type { StepKind } from "../review-flow/flow.ts";
import type { Classification } from "./classification.ts";
import type { Lala4Dispatch } from "./lala4.ts";
import type { Reversal } from "./reversal.ts";

/**
 * Una candidata que alguien descartó como duplicada de este documento
 * (`rule_out_document_duplicate`), tal como la trae `get_document_review`.
 */
export type RuledOutDuplicate =
  operations["get_document_review"]["responses"][200]["content"]["application/json"]["data"]["ruledOutDuplicates"][number];

export const RUN_STATUSES = [
  "running",
  "waiting",
  "completed",
  "failed",
  "superseded",
] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];

/** Un resultado con su nombre: el tipo de archivo, el paso actual… */
export interface Named {
  readonly key: string;
  readonly label: string;
}

/* ------------------------------------------------------------------------ */
/* La lista: `list_document_reviews`                                          */
/* ------------------------------------------------------------------------ */

export interface InvoiceReviewFilters {
  readonly status?: RunStatus;
  /** El paso en el que está ahora. */
  readonly stepKey?: string;
  /** El resultado de «Identificar qué es el archivo» (la clasificación). */
  readonly photoType?: string;
  /** El resultado de «Revisar el archivo»: `image`, `pdf`… */
  readonly fileKind?: string;
  readonly documentId?: number;
  /** Las facturas de este participante, en cualquier estado. */
  readonly participantId?: string;
  /** La bolsa: el mes de emisión (`AAAA-MM`) o el año entero (`AAAA`). */
  readonly period?: string;
  /**
   * La decisión final; `none` es «sin decidir» y `decided`, cualquiera de las
   * tres.
   */
  readonly decision?: "valid" | "invalid" | "for_data" | "none" | "decided";
  /** Quién decidió: su id de auth. */
  readonly decidedBy?: string;
  /** Decididos desde (incluido) y hasta (sin incluir), en ISO. */
  readonly decidedFrom?: string;
  readonly decidedTo?: string;
  /** Por qué falló (`FAILURE_REASONS`): solo tiene sentido con los fallidos. */
  readonly failureReason?: string;
}

/** La decisión final de una fila; `null` si no se decidió. */
export interface InvoiceReviewDecision {
  readonly outcome: string;
  /** «Válida», «Inválida»… */
  readonly label: string;
  /** El nombre de quien decidió (o su id, si no se sabe). */
  readonly by: string;
  readonly at: string;
}

export interface InvoiceReviewRow {
  readonly runId: string;
  readonly documentId: string;
  readonly flowVersion: number;
  readonly status: RunStatus;
  readonly statusLabel: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  /** Cuándo se subió. */
  readonly uploadedAt: string | null;
  readonly participantId: string | null;
  readonly resourceUrl: string;
  readonly currentStep: Named | null;
  readonly waitingSince: string | null;
  readonly fileKind: Named | null;
  /**
   * Lo que sugirió «Identificar qué es el archivo», con su confianza.
   * Ausente en un API de antes de la clasificación, y `null` en un PDF o si
   * el paso aún no corrió.
   */
  readonly photoType?: Named | null;
  /** `null` si algún precio es desconocido: NO es cero. */
  readonly costUsd: number | null;
  /** Opcional: un API de antes de `contrato-progreso.md` no la manda. */
  readonly decision?: InvoiceReviewDecision | null;
  /**
   * Por qué falló, si el seguimiento está fallido. Ausente en un API de
   * antes de reprocesar desde la página: entonces no se ofrece.
   */
  readonly failure?: Failure | null;
  /** Si la revirtieron: quién, cuándo y por qué (la misma marca que el detalle). */
  readonly reversal?: Reversal | null;
}

/**
 * Por qué falló un seguimiento, en palabras de un digitador. `reason` es una clasificación cerrada del error que
 * guardó el paso; las palabras las pone el back (`reasonLabel`).
 */
export const FAILURE_REASONS = [
  "model_limit",
  "model_unavailable",
  "timeout",
  "file",
  "invalid",
  "dispatch_lost",
  "other",
] as const;

export type FailureReason = (typeof FAILURE_REASONS)[number];

export interface Failure {
  readonly stepKey: string;
  readonly stepLabel: string;
  /** Una de `FAILURE_REASONS`; una que el front no conozca también vale. */
  readonly reason: string;
  readonly reasonLabel: string;
  /** Cuándo falló; `null` si no se sabe. */
  readonly at: string | null;
}

/** Por qué falló UN intento: va en cada intento fallido del detalle. */
export interface FailureReasonView {
  readonly reason: string;
  readonly reasonLabel: string;
}

export interface InvoiceReviewPage {
  readonly items: readonly InvoiceReviewRow[];
  readonly nextCursor: string | null;
  /** Sobre TODO lo que cumple los filtros, no solo la página. */
  readonly counts: {
    readonly total: number;
    readonly byStatus: Readonly<Record<string, number>>;
  };
}

/* ------------------------------------------------------------------------ */
/* El detalle: `get_document_review`                                          */
/* ------------------------------------------------------------------------ */

export type AttemptStatus = "scheduled" | "waiting" | "done" | "failed";

/**
 * Dónde está un intento programado en la cola de su tipo de paso: espera su
 * turno, un job lo está ejecutando, o falló una vez y espera para reintentar.
 */
export type QueueState = "queued" | "running" | "retrying";

export interface AttemptQueue {
  readonly state: QueueState;
  /** «En cola», «Trabajando» o «Por reintentar». */
  readonly stateLabel: string;
  /** Cuántos se van a tomar antes que él; `null` si no espera turno. */
  readonly ahead: number | null;
}

/** El respaldo: lo que vio el paso y por qué respondió eso. */
export interface Evidence {
  readonly summary: string;
  readonly details?: Readonly<Record<string, unknown>>;
}

export interface InvoiceReviewAttempt {
  readonly attemptId: string;
  readonly stepKey: string;
  readonly stepVersion: number;
  readonly seq: number;
  readonly status: AttemptStatus;
  readonly outcome: string | null;
  readonly output: Readonly<Record<string, unknown>> | null;
  readonly evidence: Evidence | null;
  readonly error: Readonly<Record<string, unknown>> | null;
  readonly ticket: string | null;
  readonly inputHash: string | null;
  readonly tries: number;
  readonly reason: string;
  readonly requestedBy: string;
  readonly completedBy: string | null;
  readonly superseded: boolean;
  readonly supersededAt: string | null;
  readonly supersededByAttemptId: string | null;
  readonly createdAt: string;
  readonly waitingSince: string | null;
  readonly finishedAt: string | null;
  /* Lo que agrega el contrato. */
  readonly label?: string;
  readonly kind?: StepKind;
  readonly kindLabel?: string;
  readonly outcomeLabel?: string | null;
  /** `null` si no usó IA o el precio es desconocido. */
  readonly costUsd?: number | null;
  readonly durationMs?: number | null;
  /** Cuándo empezó a trabajar (la última ejecución); `null` si no empezó. */
  readonly startedAt?: string | null;
  /** Lo que esperó su turno en la cola; `null` si no se sabe. */
  readonly queuedMs?: number | null;
  /** Lo que trabajó; `null` si no terminó o no se sabe cuándo empezó. */
  readonly workedMs?: number | null;
  /** Si está programado, dónde está en su cola ahora. */
  readonly queue?: AttemptQueue | null;
  /** Si falló: por qué, en palabras de un digitador. */
  readonly failureReason?: FailureReasonView | null;
}

export interface InvoiceReview {
  readonly runId: string;
  readonly documentId: string;
  readonly flowId: string;
  readonly flowVersion: number;
  readonly status: RunStatus;
  readonly origin: string;
  readonly previousRunId: string | null;
  readonly startedBy: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  /** En el orden en que se programaron, reemplazados incluidos. */
  readonly attempts: readonly InvoiceReviewAttempt[];
  /**
   * Lo que sugirió «Identificar qué es el archivo»; `null` en un PDF con
   * texto o si el paso no corrió.
   */
  readonly classification?: Classification | null;
  /** El envío de la decisión a lala4; `null` si aún no se envió. */
  readonly lala4Dispatch?: Lala4Dispatch | null;
  /** Si la revirtieron: quién, cuándo y por qué. Aún no lo manda el API. */
  readonly reversal?: Reversal | null;
  /** Candidatas que alguien descartó como duplicada de este documento (`rule_out_document_duplicate`), la más reciente primero. */
  readonly ruledOutDuplicates?: readonly RuledOutDuplicate[];
  /** La ficha del documento: lo que el seguimiento ya sabe de él. */
  readonly dossier: Readonly<Record<string, unknown>>;
  /* Lo que agrega el contrato. */
  readonly statusLabel?: string;
  readonly resourceUrl?: string;
  readonly costUsd?: number | null;
  /** Los pasos por los que pasó, en orden y sin los reemplazados. */
  readonly path?: readonly string[];
  /** Si el seguimiento está fallido: por qué y en qué paso. */
  readonly failure?: Failure | null;
  /**
   * El giro VIGENTE (`effectiveRotationOf`):
   * el de «Enderezar la foto», o el corregido a mano si lo hay. Ausente en
   * un API de antes: se sigue sacando de `dossier` (`rotationOf`).
   */
  readonly rotation?: 0 | 90 | 180 | 270;
  /**
   * La corrección a mano del giro, si la hay: lo que dijo la IA, lo
   * corregido, quién y cuándo. `null` sin corregir; ausente en un API de
   * antes de socio-amigo#13.
   */
  readonly rotationCorrection?: {
    readonly aiRotation: 0 | 90 | 180 | 270;
    readonly correctedRotation: 0 | 90 | 180 | 270;
    /** El id de auth: no se enseña, se usa `correctedByName`. */
    readonly correctedBy: string;
    readonly correctedByName: string;
    readonly correctedAt: string;
  } | null;
}
